import { aggregateRw, hasMixedRules, reweight } from './recomputeFeederScore';

const parseDate = (day) => {
  const [dd, mm, yyyy] = String(day).split('/').map(Number);
  return new Date(yyyy, mm - 1, dd);
};

const atMidnight = (date) => {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return d;
};

const ddmm = (date) => `${String(date.getDate()).padStart(2, '0')}/${String(date.getMonth() + 1).padStart(2, '0')}`;

const startOfWeek = (date) => {
  const d = atMidnight(date);
  const dow = (d.getDay() + 6) % 7;
  d.setDate(d.getDate() - dow);
  return d;
};

const bucketOf = (dayStr, granularity) => {
  const d = parseDate(dayStr);
  if (granularity === 'week') {
    const s = startOfWeek(d);
    const e = new Date(s);
    e.setDate(s.getDate() + 6);
    return { key: `W-${s.getTime()}`, label: `${ddmm(s)}-${ddmm(e)}`, sort: s.getTime() };
  }
  if (granularity === 'month') {
    const s = new Date(d.getFullYear(), d.getMonth(), 1);
    return { key: `M-${d.getFullYear()}-${d.getMonth()}`, label: `${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`, sort: s.getTime() };
  }
  return { key: dayStr, label: dayStr.slice(0, 5), sort: d.getTime() };
};

const number = (value, fallback = 0) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const alarmTotalForCell = (cell) => number(cell?.alarmas_total ?? cell?.reasons_summary?.alarmas_total);
const alarmUnattendedForCell = (cell) => number(cell?.alarmas_no_atendidas ?? cell?.reasons_summary?.alarmas_no_atendidas);

export const attentionPctForCell = (cell) => {
  const total = alarmTotalForCell(cell);
  if (total <= 0) return null;
  const unattended = alarmUnattendedForCell(cell);
  return (Math.max(0, total - unattended) / total) * 100;
};

export const percentile = (sortedValues, pct) => {
  if (!sortedValues.length) return null;
  if (sortedValues.length === 1) return sortedValues[0];
  const pos = (sortedValues.length - 1) * (pct / 100);
  const low = Math.floor(pos);
  const high = Math.min(low + 1, sortedValues.length - 1);
  const frac = pos - low;
  return sortedValues[low] + (sortedValues[high] - sortedValues[low]) * frac;
};

export const summarizeReactionSamples = (samples) => {
  const clean = (samples || []).map(Number).filter((value) => Number.isFinite(value)).sort((a, b) => a - b);
  if (!clean.length) return { n: 0, samples_sec: [] };
  return {
    n: clean.length,
    mean_sec: clean.reduce((sum, value) => sum + value, 0) / clean.length,
    p95_sec: percentile(clean, 95),
    max_sec: clean[clean.length - 1],
    samples_sec: clean,
  };
};

const reactionSamples = (reactionTime) => {
  if (!reactionTime || typeof reactionTime !== 'object') return [];
  if (Array.isArray(reactionTime.samples_sec)) return reactionTime.samples_sec;
  const avg = reactionTime.mean_sec ?? reactionTime.avg;
  const n = Number(reactionTime.n || 0);
  if (!Number.isFinite(Number(avg)) || n <= 0) return [];
  return Array.from({ length: n }, () => Number(avg));
};

export const buildView = (scoreData, granularity, recalcParams = null) => {
  const buckets = {};
  const nameById = {};
  Object.entries(scoreData || {}).forEach(([dayStr, rows]) => {
    const meta = bucketOf(dayStr, granularity);
    const bucket = buckets[meta.key] || (buckets[meta.key] = { ...meta, byRow: {} });
    Object.entries(rows || {}).forEach(([rowId, doc]) => {
      if (rowId === '__CENTER__') return;
      if (doc?.name && !nameById[rowId]) nameById[rowId] = doc.name;
      const acc = bucket.byRow[rowId] || (bucket.byRow[rowId] = {
        sum: 0,
        n: 0,
        days: 0,
        name: doc?.name,
        lastDay: null,
        lastDoc: null,
        food: 0,
        cycles: 0,
        alarmTotal: 0,
        alarmUnattended: 0,
        reactionSamples: [],
        rws: [],
      });
      if (doc?.name) acc.name = doc.name;
      acc.days += 1;
      if (doc && doc.score != null) {
        acc.sum += Number(doc.score);
        acc.n += 1;
      }
      acc.food += number(doc?.delivered_food_kg);
      acc.cycles += number(doc?.n_cycles);
      acc.alarmTotal += alarmTotalForCell(doc);
      acc.alarmUnattended += alarmUnattendedForCell(doc);
      acc.reactionSamples.push(...reactionSamples(doc?.reaction_time));
      if (doc?.rw && doc?.recalc_warning !== 'not_recomputable') acc.rws.push(doc.rw);
      if (!acc.lastDay || parseDate(dayStr) >= parseDate(acc.lastDay)) {
        acc.lastDay = dayStr;
        acc.lastDoc = doc;
      }
    });
  });

  const ordered = Object.values(buckets).sort((a, b) => a.sort - b.sort);
  const columns = ordered.map((b) => ({ key: b.key, label: b.label }));
  const data = {};
  ordered.forEach((bucket) => {
    data[bucket.key] = {};
    Object.entries(bucket.byRow).forEach(([rowId, acc]) => {
      const agg = granularity !== 'day' && acc.days > 1;
      const last = acc.lastDoc || {};
      const rw = agg && recalcParams?.mode === 'standard' ? aggregateRw(acc.rws, recalcParams.aggregation) : last.rw;
      const recalcScore = agg && recalcParams?.mode === 'standard' ? reweight(rw, recalcParams) : null;
      const score = recalcScore == null ? (acc.n > 0 ? acc.sum / acc.n : null) : Number(recalcScore.toFixed(2));
      data[bucket.key][rowId] = {
        ...last,
        score,
        status: score == null ? 'fasted' : (last.status || 'final'),
        name: acc.name || last.name || rowId,
        n_days: acc.days,
        agg,
        delivered_food_kg: acc.food,
        n_cycles: acc.cycles,
        alarmas_total: acc.alarmTotal,
        alarmas_no_atendidas: acc.alarmUnattended,
        reaction_time: summarizeReactionSamples(acc.reactionSamples),
        rw,
        recalc_exploratory: Boolean(last.recalc_exploratory || recalcScore != null),
        recalc_warning: recalcScore != null && hasMixedRules(rw) ? 'mixed_rules' : last.recalc_warning,
      };
    });
  });

  const rows = Array.from(new Set(ordered.flatMap((b) => Object.keys(b.byRow))))
    .sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));

  return { columns, data, rows, nameById };
};

export const buildHeaderSummary = (columns, rows, data) => {
  const scoredRows = new Set();
  const scores = [];
  const reaction = [];
  let weightedNum = 0;
  let weightedDen = 0;
  let alarmTotal = 0;
  let alarmUnattended = 0;

  columns.forEach((col) => {
    rows.forEach((rowId) => {
      const cell = data?.[col.key]?.[rowId];
      if (!cell) return;
      reaction.push(...reactionSamples(cell.reaction_time));
      alarmTotal += alarmTotalForCell(cell);
      alarmUnattended += alarmUnattendedForCell(cell);
      if (cell.score == null) return;
      const score = Number(cell.score);
      if (!Number.isFinite(score)) return;
      scoredRows.add(rowId);
      scores.push(score);
      const weight = Math.max(0, Number(cell.delivered_food_kg || 0));
      if (weight > 0) {
        weightedNum += score * weight;
        weightedDen += weight;
      }
    });
  });

  const reactionSummary = summarizeReactionSamples(reaction);
  const attended = Math.max(0, alarmTotal - alarmUnattended);
  return {
    periods: columns.length,
    scoredRows: scoredRows.size,
    totalScores: scores.length,
    scoreMean: scores.length ? scores.reduce((sum, score) => sum + score, 0) / scores.length : null,
    scoreWeighted: weightedDen > 0 ? weightedNum / weightedDen : null,
    scoreMin: scores.length ? Math.min(...scores) : null,
    scoreMax: scores.length ? Math.max(...scores) : null,
    alarmTotal,
    alarmAttended: attended,
    alarmAttendedPct: alarmTotal ? (attended / alarmTotal) * 100 : null,
    reactionMean: reactionSummary.mean_sec ?? null,
    reactionP95: reactionSummary.p95_sec ?? null,
  };
};

export const buildRangeRanking = (columns, rows, data, rankingBy = 'score') => rows
  .map((rowId) => {
    let sum = 0;
    let n = 0;
    let latestScored = null;
    let alarmTotal = 0;
    let alarmUnattended = 0;
    let latestAttention = null;
    const samples = [];
    let latestReaction = null;

    columns.forEach((col) => {
      const value = data?.[col.key]?.[rowId];
      if (!value) return;

      const attentionPct = attentionPctForCell(value);
      if (attentionPct != null) {
        alarmTotal += alarmTotalForCell(value);
        alarmUnattended += alarmUnattendedForCell(value);
        latestAttention = { col, value };
      }

      const cellSamples = reactionSamples(value.reaction_time);
      if (cellSamples.length) {
        samples.push(...cellSamples);
        latestReaction = { col, value };
      }

      if (value.score == null || !Number.isFinite(Number(value.score))) return;
      sum += Number(value.score);
      n += 1;
      latestScored = { col, value };
    });

    if (rankingBy === 'attention') {
      if (!alarmTotal) return null;
      const latestValue = latestAttention?.value || {};
      const attended = Math.max(0, alarmTotal - alarmUnattended);
      const attentionPct = (attended / alarmTotal) * 100;
      return {
        rowId,
        selectCol: latestAttention?.col?.key,
        value: { ...latestValue, score: attentionPct, attentionPct, alarmas_total: alarmTotal, alarmas_no_atendidas: alarmUnattended, name: latestValue.name || rowId },
      };
    }

    if (rankingBy === 'reaction') {
      const reactionSummary = summarizeReactionSamples(samples);
      if (!reactionSummary.n || reactionSummary.mean_sec == null) return null;
      const latestValue = latestReaction?.value || {};
      return {
        rowId,
        selectCol: latestReaction?.col?.key,
        value: { ...latestValue, score: reactionSummary.mean_sec, reaction_time: reactionSummary, name: latestValue.name || rowId },
      };
    }

    if (!n) return null;
    const latestValue = latestScored?.value || {};
    return {
      rowId,
      selectCol: latestScored?.col?.key,
      value: { ...latestValue, score: sum / n, name: latestValue.name || rowId },
    };
  })
  .filter(Boolean)
  .sort((a, b) => (rankingBy === 'reaction' ? a.value.score - b.value.score : b.value.score - a.value.score));
